<template>
    <div class="ittg" role="tablist" :aria-label="t('investmentStakingToggle')">
        <!-- sliding pill indicator -->
        <div
            class="ittg-slider"
            :class="activeType === 'staking' ? 'ittg-slider--r' : ''"
            :style="`background:linear-gradient(135deg,${themeColors.primary},${themeColors.primaryDark});box-shadow:0 0 22px rgba(${themeColors.primaryRgb},0.38);`"
            aria-hidden="true"
        ></div>

        <button
            class="ittg-tab"
            :class="activeType === 'investment' ? 'ittg-tab--on' : ''"
            @click="$emit('update:activeType','investment')"
            role="tab"
            :aria-selected="activeType === 'investment'"
            :aria-pressed="activeType === 'investment'"
            :aria-label="t('switchToInvestmentPlans')"
        >
            <span class="ittg-tab-ic">
                <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                </svg>
            </span>
            <span class="ittg-tab-lbl">{{ t('investmentPlans') }}</span>
            <span v-if="activeType === 'investment'" class="ittg-tab-badge" :style="`background:rgba(0,0,0,0.18);`">
                <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
            </span>
        </button>

        <button
            class="ittg-tab"
            :class="activeType === 'staking' ? 'ittg-tab--on' : ''"
            @click="$emit('update:activeType','staking')"
            role="tab"
            :aria-selected="activeType === 'staking'"
            :aria-pressed="activeType === 'staking'"
            :aria-label="t('switchToStakingPools')"
        >
            <span class="ittg-tab-ic">
                <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"/>
                </svg>
            </span>
            <span class="ittg-tab-lbl">{{ t('stakingPools') }}</span>
            <span v-if="activeType === 'staking'" class="ittg-tab-badge" :style="`background:rgba(0,0,0,0.18);`">
                <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
            </span>
        </button>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useTranslation } from '@/composables/useTranslation'

defineProps({
    activeType: { type: String, required: true, validator: v => ['investment','staking'].includes(v) }
})
defineEmits(['update:activeType'])

const page = usePage()
const { t } = useTranslation()
const primaryColor = computed(() => page.props.primaryColor || '#a75ded')

const hexToRgb = (hex) => {
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return r ? { r:parseInt(r[1],16),g:parseInt(r[2],16),b:parseInt(r[3],16) } : {r:167,g:93,b:237}
}
const rgbToHex = (r,g,b) => '#'+((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1)
const adjustColor = (hex,amt) => {
    const {r,g,b} = hexToRgb(hex)
    return rgbToHex(Math.min(255,Math.max(0,r+amt)),Math.min(255,Math.max(0,g+amt)),Math.min(255,Math.max(0,b+amt)))
}
const themeColors = computed(() => {
    const {r,g,b} = hexToRgb(primaryColor.value)
    return { primary:primaryColor.value, primaryDark:adjustColor(primaryColor.value,-25), primaryRgb:`${r},${g},${b}` }
})
</script>

<style scoped>
.ittg {
    position: relative;
    display: inline-flex;
    align-items: center;
    background: rgba(8,3,18,0.96);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 16px;
    padding: 5px;
    gap: 0;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(255,255,255,0.03);
    margin: 0 auto;
}

/* Sliding indicator */
.ittg-slider {
    position: absolute;
    top: 5px; bottom: 5px; left: 5px;
    width: calc(50% - 5px);
    border-radius: 11px;
    transition: transform 0.32s cubic-bezier(0.34,1.56,0.64,1);
    z-index: 0;
    pointer-events: none;
}
.ittg-slider--r { transform: translateX(100%); }

/* Tab buttons */
.ittg-tab {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 10px 20px;
    border-radius: 11px;
    font-size: 13px;
    font-weight: 700;
    color: rgba(148,163,184,0.6);
    background: transparent;
    border: none;
    cursor: pointer;
    outline: none;
    white-space: nowrap;
    transition: color 0.2s ease;
    flex: 1;
    justify-content: center;
    min-width: 0;
}
.ittg-tab--on { color: #0f172a; }
.ittg-tab:not(.ittg-tab--on):hover { color: rgba(255,255,255,0.85); }
.ittg-tab:focus-visible { outline: 2px solid rgba(167,93,237,0.55); outline-offset: 2px; border-radius: 11px; }

.ittg-tab-ic { display: flex; align-items: center; flex-shrink: 0; }
.ittg-tab-lbl { line-height: 1; }
.ittg-tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px; height: 18px;
    border-radius: 5px;
    color: rgba(0,0,0,0.6);
    flex-shrink: 0;
}

@media(max-width: 420px) {
    .ittg-tab { padding: 9px 14px; font-size: 12px; gap: 5px; }
    .ittg-tab-badge { display: none; }
}
@media(prefers-reduced-motion: reduce) {
    .ittg-slider { transition: none; }
}
</style>
